import { ExternalLink, Download } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader } from "@/components/ui/loader";

import {
  useGetPaymentRemittanceDownloadLinks,
  type PaymentRemittanceDownloadLinkSourcePdf,
} from "@/lib/api/paymentRemittancePdfs";

interface DownloadLinksDialogProps {
  batchId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * On-open, fetches presigned-GET URLs for the batch's source PDFs and
 * (when present) the active assembled PDF. URLs expire — we never
 * cache between dialog opens (see paymentRemittancePdfs.ts).
 */
export function DownloadLinksDialog({
  batchId,
  open,
  onOpenChange,
}: DownloadLinksDialogProps) {
  const linksResult = useGetPaymentRemittanceDownloadLinks({
    batchId,
    enabled: open,
  });

  const data = linksResult?.data?.data ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Download links
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {linksResult.isLoading || linksResult.isFetching ? (
            <div className="flex flex-row items-center gap-2 text-sm text-muted-foreground">
              <Loader />
            </div>
          ) : !linksResult.isSuccess || !data ? (
            <div className="rounded border border-destructive bg-destructive/10 p-2 text-sm text-destructive">
              {linksResult?.data?.message ?? "Failed to load download links"}
            </div>
          ) : (
            <>
              <FinalAssembledSection data={data.final_assembled} />
              <SourcePdfsSection sourcePdfs={data.source_pdfs} />
              <div className="text-xs text-muted-foreground">
                URLs expire in {Math.round(data.expires_in_seconds / 60)}{" "}
                minutes.
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FinalAssembledSection({
  data,
}: {
  data: {
    assembly_version_id: string;
    version_number: number;
    status: string;
    filename: string;
    presigned_get_url: string;
    presigned_download_url: string;
  } | null;
}) {
  return (
    <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
      <div className="text-sm font-semibold text-muted-foreground">
        Final assembled PDF
      </div>
      {!data ? (
        <div className="text-sm text-muted-foreground">
          No active assembled version yet.
        </div>
      ) : (
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-xs break-all">
              {data.filename}
            </span>
            <span className="text-xs text-muted-foreground">
              v{data.version_number} · status{" "}
              <span className="font-mono">{data.status}</span>
            </span>
          </div>
          <div className="flex flex-row gap-2">
            <a
              href={data.presigned_get_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </a>
            <a
              href={data.presigned_download_url}
              download={data.filename}
              className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
            >
              <Download className="h-3 w-3" />
              Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function SourcePdfsSection({
  sourcePdfs,
}: {
  sourcePdfs: PaymentRemittanceDownloadLinkSourcePdf[];
}) {
  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="text-sm font-semibold text-muted-foreground">
        Source PDFs ({sourcePdfs.length})
      </div>
      {sourcePdfs.length === 0 ? (
        <div className="text-sm text-muted-foreground">No source PDFs.</div>
      ) : (
        <ul className="divide-y">
          {sourcePdfs.map((sp: PaymentRemittanceDownloadLinkSourcePdf) => (
            <li
              key={sp.source_pdf_id}
              className="flex flex-row items-center justify-between gap-2 py-2"
            >
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-mono text-xs break-all">
                  {sp.original_filename}
                </span>
                <span className="text-xs text-muted-foreground">
                  #{sp.upload_order_index} · status{" "}
                  <span className="font-mono">{sp.processing_status}</span>
                </span>
              </div>
              <div className="flex flex-row gap-2 shrink-0">
                <a
                  href={sp.presigned_get_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open
                </a>
                <a
                  href={sp.presigned_get_url}
                  download={sp.original_filename}
                  className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
                >
                  <Download className="h-3 w-3" />
                  Download
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
